import React, { ChangeEvent, FunctionComponent, ReactNode, useState } from 'react';
import SectionHeader from 'shared/components/atoms/SectionHeader';
import ToggleButton from 'shared/components/molecules/ToggleButton';
import Column from 'shared/components/atoms/Column';
import Button, { ButtonMainStyle } from 'shared/components/atoms/Button';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedCompanyId } from 'User/selectors';
import { cloneDeep } from 'lodash';
import Spinner from 'shared/components/atoms/Spinner';
import Alert from 'shared/components/atoms/Alert';
import { SupplierList } from 'Admin/preferences/SupplierPreferences/SupplierPreferences';
import { Supplier } from 'Quotes/types/supplier';
import { presentSupplierName } from 'Quotes/utils/supplierHelpers';
import { updateCustomerPreferences } from 'Admin/crud/updateCustomerPreferences';

const mergedSuppliers: Record<string, Supplier[]> = {
  ITS: [Supplier.ITS_FIBRELIGHT, Supplier.ITS_FIBREBRIGHT],
};

type Props = {
  className?: string;
  introText: ReactNode;
  suppliers: SupplierList[];
  updatePageStatus(readOnly: boolean, saved: boolean): void;
  updateSuppliersState(displaySupplierListState: SupplierList[]): void;
  cloudConnectProviders?: SupplierList[];
  updateCloudConnectProvidersState(cloudConnectProviders: SupplierList[]): void;
};

export const EditPreferences: FunctionComponent<React.PropsWithChildren<Props>> = ({
  className,
  introText,
  suppliers,
  updatePageStatus,
  updateSuppliersState,
  cloudConnectProviders,
  updateCloudConnectProvidersState
}) => {
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState(false);

  const companyId = useSelector(selectSelectedCompanyId);
  const dispatch = useDispatch();
  const columns = 3;
  const localSuppliers = cloneDeep(suppliers);
  const localCloudConnectProviders = cloneDeep(cloudConnectProviders) ?? [];

  const onToggleSupplierChange = (event: ChangeEvent<HTMLInputElement>) => {
    // string value of "Key" in the object inside of suppliers array
    const supplierKey = event.target.getAttribute('data-key');
    // Index of this changed value in the suppliers array
    const supplierIndex = suppliers.findIndex((element) => {
      return element.key === supplierKey;
    });

    localSuppliers[supplierIndex].status = event.target.checked;
  };

  const onToggleCloudConnectProviderChange = (event: ChangeEvent<HTMLInputElement>) => {
    // string value of "Key" in the object inside of suppliers array
    const providerKey = event.target.getAttribute('data-key');
    // Index of this changed value in the suppliers array
    const providerIndex = cloudConnectProviders?.findIndex((element) => {
      return element.key === providerKey;
    });

    localCloudConnectProviders[providerIndex!].status = event.target.checked;
  };

  const onCancelButtonClick = () => {
    updatePageStatus(true, false);
  };

  const onSubmitButtonClick = () => {
    if (pending) {
      return;
    }

    const selectedSupplierValues = localSuppliers
      .filter((localSupplier) => localSupplier.status)
      .flatMap((localSupplier) => mergedSuppliers[localSupplier.key] ?? [localSupplier.value]);
    
    const selectedCloudConnectProviderValues = localCloudConnectProviders
      .filter((localCloudConnectProvider) => localCloudConnectProvider.status)
      .map((localCloudConnectProvider) => localCloudConnectProvider.key);

    setPending(true);

    updateCustomerPreferences(companyId, selectedSupplierValues, selectedCloudConnectProviderValues)
      .then(() => {
        updateSuppliersState(localSuppliers);
        updateCloudConnectProvidersState(localCloudConnectProviders);
        updatePageStatus(true, true);
        // Dispatch refresh action here
        dispatch({ type: 'FORCE_REFRESH_COMPANY_PREFERENCES', payload: { id: companyId } });
      })
      .catch(() => {
        setErrors(true);
      })
      .finally(() => {
        setPending(false);
      });
  };

  return (
    <div className={className}>
      <div className="row no-gutters button-wrapper">
        <div className="col-4 offset-8">
          <Button
            data-testid="preferences-button-edit-cancel"
            mainStyle={ButtonMainStyle.PrimaryOutline}
            disabled={pending}
            onClick={onCancelButtonClick}
          >
            Cancel
          </Button>
          <Button
            data-testid="preferences-button-edit-submit"
            type="submit"
            mainStyle={ButtonMainStyle.PrimaryRectangular}
            disabled={pending}
            onClick={onSubmitButtonClick}
          >
            {pending && <Spinner size="small" colour="secondary" />}
            {!pending && 'Save changes'}
          </Button>
        </div>
      </div>

      <SectionHeader text="Supplier Preferences" />

      {errors && <Alert>Unable to save changes</Alert>}

      {introText}

      <SectionHeader text="Last Mile Providers" className="mt-5"/>
      <Column defaultWidth={12}>
        <div data-testid="preferences-toggles" className="row no-gutters">
          {[...Array(columns).keys()].map((c) => {
            const suppliersInColumn = suppliers.filter((_, i) => i % columns === c);
            return (
              <Column key={'col' + c} defaultWidth={12 / columns} classNames={['admin']}>
                {suppliersInColumn.map((supplier, i_inner) => (
                  <div key={'col' + c + '-' + i_inner} className="row no-gutters">
                    <ToggleButton
                      toggleKey={supplier.key}
                      label={supplier.key === 'ITS' ? 'ITS' : presentSupplierName(supplier.value)}
                      defaultChecked={supplier.status}
                      onToggleChange={onToggleSupplierChange}
                    />
                  </div>
                ))}
              </Column>
            );
          })}
        </div>
      </Column>

      {cloudConnectProviders && cloudConnectProviders.length > 0 && (
        <>
          <SectionHeader text="Cloud Connect Providers" className="mt-5"/>
          <Column defaultWidth={12}>
            <div data-testid="cloud-connect-providers" className="row no-gutters">
              {[...Array(columns).keys()].map((c) => {
                const providersInColumn = cloudConnectProviders.filter((_, i) => i % columns === c);
                return (
                  <Column key={'cloud-col' + c} defaultWidth={12 / columns} classNames={['admin']}>
                    {providersInColumn.map((provider, i_inner) => (
                      <div key={'cloud-col' + c + '-' + i_inner} className="row no-gutters">
                        <ToggleButton
                          toggleKey={provider.key}
                          label={provider.value}
                          defaultChecked={provider.status}
                          onToggleChange={onToggleCloudConnectProviderChange}
                        />
                      </div>
                    ))}
                  </Column>
                );
              })}
            </div>
          </Column>
        </>
      )}
    </div>
  );
};
